import React, { useEffect, useState } from 'react';
import { AlertCircle, ChevronDown, ChevronUp, History } from 'lucide-react';
import { getReviewHistory } from '../../services/reviewWorkflowApi';
import ReviewHistory from './ReviewHistory';
import { deciderOf, fmt, ts } from './reviewHistoryShared';

/**
 * "Changes requested" banner shown at the top of the assessment editor whenever
 * the assessment sits in the `denied` review state.
 *
 * This is the payoff of a rejection notification: the submitter clicks through
 * from the bell and lands here with the reviewer's reason already on screen, so
 * they can start making the required changes without hunting through the review
 * history modal. It surfaces the latest denial's reason, who requested it, and
 * when — pulled from the same ReviewSubmission records the History modal uses.
 *
 * The banner is not manually dismissible: it clears on its own once the
 * assessment leaves the `denied` state (i.e. after the user resubmits), so a
 * pending change request can never be permanently hidden and forgotten.
 *
 * Props:
 *   assessmentId - numeric assessment pk (object_id for the history endpoint)
 *   entityName   - assessment name, for the history modal header
 *   status       - current review status; banner renders only when 'denied'
 */
const latestDenial = (rows) => {
  const denials = (rows || [])
    .filter((r) => r.status === 'denied' && r.is_decided && r.decision_reason)
    .sort((a, b) => ts(b.decided_at) - ts(a.decided_at));
  return denials[0] || null;
};

const ChangesRequestedBanner = ({ assessmentId, entityName, status }) => {
  const [denial, setDenial] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);

  const isDenied = status === 'denied';

  useEffect(() => {
    if (!isDenied || !assessmentId) {
      setDenial(null);
      return undefined;
    }
    let active = true;
    (async () => {
      try {
        const data = await getReviewHistory('assessment', assessmentId);
        const rows = Array.isArray(data) ? data : data?.results || [];
        if (active) setDenial(latestDenial(rows));
      } catch {
        // Non-fatal: the status pill still tells the user changes were requested.
        if (active) setDenial(null);
      }
    })();
    return () => {
      active = false;
    };
  }, [isDenied, assessmentId]);

  if (!isDenied) return null;

  const who = denial ? deciderOf(denial).name : null;
  const when = denial?.decided_at ? fmt(denial.decided_at) : null;

  return (
    <div className="border-b border-danger-100 bg-danger-50 text-danger-700 dark:border-danger-500/30 dark:bg-danger-500/10 dark:text-danger-400">
      <div className="flex items-start gap-2.5 px-4 py-2.5 text-[13px]">
        <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-semibold text-danger-800 dark:text-danger-400">Changes requested</span>
            {(who || when) && (
              <span className="text-[12.5px] tabular-nums opacity-80">
                {who ? `by ${who}` : ''}
                {who && when ? ' · ' : ''}
                {when || ''}
              </span>
            )}
            {denial?.decision_reason && (
              <button
                onClick={() => setExpanded((v) => !v)}
                className="ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[12.5px] font-medium transition-colors hover:bg-danger-100 dark:hover:bg-danger-500/15"
                title={expanded ? 'Hide reason' : 'Show reason'}
              >
                {expanded ? <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />}
                {expanded ? 'Hide' : 'Show'} reason
              </button>
            )}
          </div>
          {expanded && denial?.decision_reason && (
            <p className="mt-1.5 whitespace-pre-wrap break-words text-[13px] leading-relaxed text-danger-800 dark:text-gray-200">
              {denial.decision_reason}
            </p>
          )}
          <div className="mt-1">
            <button
              onClick={() => setHistoryOpen(true)}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-danger-700 underline-offset-4 transition-colors hover:text-danger-800 hover:underline dark:text-danger-400"
            >
              <History className="h-3.5 w-3.5" aria-hidden="true" />
              View full history
            </button>
          </div>
        </div>
      </div>

      {historyOpen && (
        <ReviewHistory
          entityType="assessment"
          objectId={assessmentId}
          entityName={entityName}
          currentStatus={status}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </div>
  );
};

export default ChangesRequestedBanner;
